import * as THREE from 'three'

// ── Soft circle sprite ─────────────────────────────────────────────────────────
function makeSpriteTex() {
  const s   = 32
  const cvs = document.createElement('canvas')
  cvs.width = cvs.height = s
  const ctx  = cvs.getContext('2d')
  const grad = ctx.createRadialGradient(s/2, s/2, 0, s/2, s/2, s/2)
  grad.addColorStop(0,   'rgba(255, 255, 255, 1)')
  grad.addColorStop(0.25,'rgba(220, 220, 255, 0.4)')
  grad.addColorStop(1,   'rgba(0,   0,   0,   0)')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, s, s)
  return new THREE.CanvasTexture(cvs)
}

// ── Floating dust particles ────────────────────────────────────────────────────
export function buildParticles(scene) {
  const COUNT = window.innerWidth <= 1024 ? 140 : 220

  const base      = new Float32Array(COUNT * 3)   // resting positions
  const phases    = new Float32Array(COUNT)        // per-particle sin phase
  const speeds    = new Float32Array(COUNT)        // per-particle drift speed
  const positions = new Float32Array(COUNT * 3)   // live positions (updated each frame)

  for (let i = 0; i < COUNT; i++) {
    // Scatter in a loose ellipsoid around the model
    const theta = Math.random() * Math.PI * 2
    const phi   = Math.acos(2 * Math.random() - 1)
    const r     = 2.2 + Math.random() * 5.0

    base[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta)
    base[i * 3 + 1] = (Math.random() - 0.5) * 7
    base[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta)

    phases[i] = Math.random() * Math.PI * 2
    speeds[i] = 0.25 + Math.random() * 0.45
  }

  positions.set(base)

  const geo     = new THREE.BufferGeometry()
  const posAttr = new THREE.BufferAttribute(positions, 3)
  posAttr.setUsage(THREE.DynamicDrawUsage)
  geo.setAttribute('position', posAttr)

  const mat = new THREE.PointsMaterial({
    size:        0.032,
    map:         makeSpriteTex(),
    transparent: true,
    blending:    THREE.NormalBlending,   // light theme: additive washes out on white
    depthWrite:  false,
    color:       new THREE.Color('#6a4fc8'),
    opacity:     0.38,
  })

  const points = new THREE.Points(geo, mat)
  points.frustumCulled = false   // positions update every frame — stale bounding sphere would cull at VFX_Y
  scene.add(points)

  let currentY = 0  // smoothly tracked camera Y

  function update(elapsed, rotationY = 0, camY = 0, sizeMultiplier = 1, spreadMult = 1, opacity = 0.38, snapY = false) {
    // In VFX/Contact the camera snaps far down — use instant tracking to
    // prevent particles lagging above the viewport for many frames
    const trackSpeed = snapY ? 1.0 : 0.12
    currentY += (camY - currentY) * trackSpeed

    // Scale particle size and opacity per section
    mat.size       = 0.065 * sizeMultiplier
    mat.opacity    = opacity
    mat.depthTest  = !snapY   // disable depth test in VFX/Contact so nothing occludes them

    const cosR = Math.cos(rotationY * 0.12)
    const sinR = Math.sin(rotationY * 0.12)
    for (let i = 0; i < COUNT; i++) {
      const p = phases[i]
      const s = speeds[i]
      // Scale wave amplitude with spread so motion stays proportional
      const wAmp = 0.35 * spreadMult
      const wx = Math.sin(elapsed * s       + p       ) * wAmp
      const wy = Math.sin(elapsed * s * 0.6 + p + 1.0 ) * 0.45
      const wz = Math.cos(elapsed * s       + p + 0.5 ) * wAmp
      // Spread base positions outward in XZ — fills the viewport at larger camera distances
      const bx = base[i * 3 + 0] * spreadMult
      const bz = base[i * 3 + 2] * spreadMult
      positions[i * 3 + 0] = bx * cosR - bz * sinR + wx
      positions[i * 3 + 1] = currentY + base[i * 3 + 1] * spreadMult + wy
      positions[i * 3 + 2] = bx * sinR + bz * cosR + wz
    }
    posAttr.needsUpdate = true
  }

  return { update }
}

// ── Glow ring beneath the model ────────────────────────────────────────────────
export function buildGlowRing(scene) {
  // Outer soft halo disc — large, very transparent
  const haloGeo = new THREE.CircleGeometry(2.4, 64)
  const haloMat = new THREE.MeshBasicMaterial({
    color:       new THREE.Color('#5522cc'),
    transparent: true,
    opacity:     0.08,
    blending:    THREE.AdditiveBlending,
    depthWrite:  false,
    side:        THREE.DoubleSide,
  })
  const halo = new THREE.Mesh(haloGeo, haloMat)
  halo.rotation.x = -Math.PI / 2
  halo.position.y = -1.45
  scene.add(halo)

  // Inner sharp ring
  const ringGeo = new THREE.RingGeometry(1.1, 1.45, 64)
  const ringMat = new THREE.MeshBasicMaterial({
    color:       new THREE.Color('#8844ff'),
    transparent: true,
    opacity:     0.22,
    blending:    THREE.AdditiveBlending,
    depthWrite:  false,
    side:        THREE.DoubleSide,
  })
  const ring = new THREE.Mesh(ringGeo, ringMat)
  ring.rotation.x = -Math.PI / 2
  ring.position.y = -1.44
  scene.add(ring)

  function update(elapsed) {
    // Slow breathe
    const breathe = Math.sin(elapsed * 0.7)
    halo.material.opacity     = 0.06 + breathe * 0.03
    ring.material.opacity     = 0.18 + breathe * 0.07
    const s = 1 + breathe * 0.04
    halo.scale.set(s, 1, s)
    ring.scale.set(s, 1, s)
  }

  return { update }
}
